/**
 * The double materiality assessment, plotted.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { readArray, readNumber, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import {
  newId,
  newMaterialityEntry,
  type MaterialityEntry,
  type MaterialitySection,
} from '@/domain/section'
import MaterialityBlock from '@/sections/materiality/MaterialityBlock.vue'
import MaterialityInspector from '@/sections/materiality/MaterialityInspector.vue'
import type { SectionDefinition } from '@/sections/types'

/** A score, kept inside the scale the matrix is drawn on. */
function score(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(5, Math.max(0, value))
}

function readEntry(value: JsonValue): MaterialityEntry {
  return {
    id: readString(value, 'id') || newId(),
    topic: readString(value, 'topic'),
    impact: score(readNumber(value, 'impact', 3)),
    financial: score(readNumber(value, 'financial', 3)),
  }
}

export const materialitySection: SectionDefinition<'materiality', MaterialitySection> = {
  kind: 'materiality',
  band: 'sustainability',

  icon: ['M3 13V3', 'M3 13h10', 'M6 10.5a0.9 0.9 0 1 0 0.01 0', 'M10 6a0.9 0.9 0 1 0 0.01 0'],

  create: (id, title) => ({
    id,
    kind: 'materiality',
    title,
    entries: [newMaterialityEntry(), newMaterialityEntry(), newMaterialityEntry()],
    threshold: 2.5,
    caption: '',
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'materiality',
    title,
    entries: readArray(stored, 'entries').map(readEntry),
    threshold: score(readNumber(stored, 'threshold', 2.5)),
    caption: readString(stored, 'caption'),
  }),

  block: MaterialityBlock,
  inspector: MaterialityInspector,
}
